import {
    core,
    TranslateService,
    Common,
    EventBusService,
    UserService,
    AppConfigFactory,
    APP_CONFIG,
    StateService, BaseIssuesService } from '@icc/shared';
import {
    InfoService,
    isUndefined,
    isArray,
    IssueLevel,
} from '@icc/shared/helpers';
import { StepsService } from '@icc/configurator/shared';
import { ConfiguratorsDataService } from '@icc/shared/configurators/configurators-data.service';
import { ConfigurationsService } from '@icc/configurator/shared';
import { CurrentConfiguratorService } from '@icc/configurator/shared';
import { PriceService, DiscountsAndMultipliersService, PriceBaseService } from '@icc/price/b2b';
import { PriceAccessoryService } from '../+price';
import { IccAccessory, IccAccessoryAccessory, IccAccessoryColor } from '@icc/construction-elements/types';
import { Inject, Injectable } from '@angular/core';
import { AccessoriesListPageComponent } from '../accessories-list-page/accessories-list-page.component';
import { CustomPricesService } from '@icc/shared/custom-price/custom-prices.service';
import { RollerShutterActiveConfiguration } from '@icc/shutter/types';
import { TimeLimitService } from '@icc/configurator/shared';
import { WindowActiveConfiguration, DoorActiveConfiguration } from '@icc/window-door/types';
import { MosquitoActiveConfiguration } from '@icc/mosquito-screen/types';
import { NewColorsService } from '../colors/new-colors.service';
import { ColorMappingService } from '../colors/colors-mapping.service';
import { WoodService } from '../wood.service';
import { ModalService } from '@icc/configurator/shared';
import '@icc/garage-door/types';

@Injectable({
    providedIn: 'root',
})
export class AccessoriesService {
    protected configurators = [
        'window',
        'hs',
        'door',
        'folding_door',
        'sliding_door',
        'accessory',
        'garage_door',
        'roller_shutter',
        'external_blind',
        'mosquito',
    ];
    protected allAccessories: IccAccessoryAccessory[] = [];
    protected categories: any[] = [];
    protected subcategories: any[] = [];
    colorsAll: IccAccessoryColor[] = [];
    loadedData = false;

    constructor(
        protected infoService: InfoService,
        protected modalService: ModalService,
        protected configurationsService: ConfigurationsService<
            | 'window'
            | 'roller_shutter'
            | 'external_blind'
            | 'door'
            | 'accessory'
            | 'mosquito'
            | 'garage_door'
        >,
        protected currentConfiguratorService: CurrentConfiguratorService,
        protected configuratorsDataService: ConfiguratorsDataService,
        protected priceService: PriceService,
        protected priceAccessoryService: PriceAccessoryService,
        protected priceBaseService: PriceBaseService,
        protected stepService: StepsService,
        protected newColorsService: NewColorsService,
        protected eventBusService: EventBusService,
        protected timeLimitService: TimeLimitService,
        protected translateService: TranslateService,
        protected userService: UserService,
        protected customPricesService: CustomPricesService,
        @Inject(APP_CONFIG) protected config: AppConfigFactory,
        protected discountsAndMultipliersService: DiscountsAndMultipliersService,
        protected stateService: StateService,
        protected woodService: WoodService,
        protected issuesService: BaseIssuesService,
        protected colorMappingService: ColorMappingService
    ) {
        'ngInject';

        if (this.configuratorsDataService.loaded) {
            this.init();
        }

        this.eventBusService.subscribeWithoutConfiguration('initializedConfigurator', () => {
            this.init();
        });

        this.eventBusService.subscribe(['changedShutter', 'changedDrive'], (data) => {
            this.validateShutterDriveDependantAccessories(
                data.activeConfiguration as RollerShutterActiveConfiguration
            );
        });

        this.eventBusService.subscribe(['setConstructionColor'], (data) => {
            this.reApplyDefaultColors(data.activeConfiguration);
        });

        this.eventBusService.subscribe(['accessoryColorBasedOnMapping', 'changedDoorHardware'], () => {
            this.reApplyDefaultColors(this.configurationsService.conf.Current);
        });
    }

    /**
     * Funkcja inicjalizujaca
     */
    init() {
        if (this.configurators.indexOf(this.currentConfiguratorService.conf) === -1) {
            return;
        }
        // wszystkie akcesorie połączone (sash, sides i conf)
        this.allAccessories = this.configuratorsDataService.data.windowAccessories;
        this.subcategories =
            this.configuratorsDataService.data.windowAccessoriesSubCategories || [];
        this.colorsAll = this.configuratorsDataService.data.windowHandlesColors;
        this.categories = this.filterWindowAccessoriesCategories(
            this.configuratorsDataService.data.windowAccessories
        );

        this.updateStepVisibility();
        this.loadedData = true;

        if (this.currentConfiguratorService.conf === 'accessory') {
            this.configurationsService.conf.Current.Name = this.translateService.instant(
                'ACCESSORY|Akcesoria'
            );
        }
    }

    /**
     * Funkcja znajdujaca akcesoria
     */
    findAccessories(conf = this.configurationsService.conf.Current, confType = conf.type) {
        if (conf.type === 'garage_door') {
            return {accessories: []};
        }
        if (
            Common.isUndefined(conf) ||
            this.configurators.indexOf(this.configurationsService.conf.conf) === -1
        ) {
            return {accessories: []};
        }
        const accessories = [];
        const sides = ['top', 'bottom', 'left', 'right'];
        let i = 0;

        Common.forEach(this.allAccessories, (accessory) => {
            const availableSystemsIds = (accessory.window_lines_ids || []).map(Number);
            if (
                (WindowActiveConfiguration.is(conf) || RollerShutterActiveConfiguration.is(conf)) &&
                (confType === 'roller_shutter' || confType === 'external_blind')
            ) {
                if (
                    Common.isDefined(conf.RollerShutter.type.id) &&
                    availableSystemsIds.indexOf(Number(conf.RollerShutter.type.id)) > -1
                ) {
                    accessories.push(accessory);
                }
            }

            if (
                confType === 'mosquito' &&
                MosquitoActiveConfiguration.is(conf) &&
                Common.isDefined(conf.System.id) &&
                availableSystemsIds.indexOf(+conf.System.id) > -1
            ) {
                accessories.push(accessory);
            }
        });

        return {accessories};
    }

    updateStepVisibility() {
        const { accessories } = this.findAccessories();
        if (accessories.length > 0 || this.configurationsService.conf.conf === 'garage_door') {
            this.stepService.enable('accessories');
        } else {
            this.stepService.disable('accessories');
        }
    }

    /**
     * Aktualizacja danych w akcesoriach
     *
     * @param {any} place Miejsce
     * @param {any} accessory Akcesoria
     */
    updateAccessories(conf = this.configurationsService.conf.Current) {
        conf.Accessories = conf.Accessories.filter((el) => this.filterAccessory(el, conf));
        this.priceService.count();
    }

    /**
     * Funkcja filtra akcesoriów
     * @param {object} el Akcesoium
     * @return {boolean}  Czy pasuuje do filtra
     */
    filterAccessory(el, conf = this.configurationsService.conf.Current) {
        const availableSystemsIds = (el.accessory.window_lines_ids || []).map(Number);
        if (conf.type === 'roller_shutter' || conf.type === 'external_blind') {
            if (
                Common.isDefined(conf.RollerShutter.type.id) &&
                availableSystemsIds.indexOf(Number(conf.RollerShutter.type.id)) > -1
            ) {
                return true;
            }
        } else if (
            conf.type === 'accessory'
            || 'System' in conf && (Common.isDefined(conf.System.id)
                && availableSystemsIds.indexOf(+conf.System.id) > -1)
        ) {
            return true;
        }

        return false;
    }

    /**
     * Funkcja dodajaca
     * @param {object} place     Miejsce
     * @param {object} accessory Akcesoria
     * @param {object} color     Kolor
     */
    // eslint-disable-next-line max-statements
    add(place, accessory, color?, conf = this.configurationsService.conf.Current) {
        let count = Number(accessory.count);
        count = count && count >= 1 ? count : 1;
        const colorOptions =
            accessory.show_colors && accessory.price_source === 'table'
                ? accessory.colorOptions
                : null;

        const filteredCategories = this.filterWindowAccessoriesCategories(
            this.configuratorsDataService.data.windowAccessoriesCategories
        );
        const category = filteredCategories.filter(
            (e) => e.id === accessory.window_accessories_category_id
        );
        const subcategory = this.configuratorsDataService.data.windowAccessoriesSubCategories.filter(
            (e) => e.id === accessory.window_accessories_category_id
        );
        // pozycja środka nawiewnika
        let position = null;
        if (accessory.type === 'ventilator' || accessory.type === 'ventilator_hole') {
            position =
                Number(accessory.position) || 1;
        }

        let accessoriesArr = conf.Accessories;
        const forIt = 'construction';

        /** Można dodać tylko jeden dodatek z tej grupy */
        let accessoriesFromSameCategory =
            Common.isDefined(category[0]) && category[0].only_one_accessory
                ? accessoriesArr.filter(
                      (el) =>
                          el.accessory.window_accessories_category_id === category[0].id &&
                          el.accessory.id !== accessory.id
                  )
                : [];

        if (accessoriesFromSameCategory.length) {
            return;
        }

        if (!accessoriesFromSameCategory.length) {
            accessoriesFromSameCategory =
                Common.isDefined(subcategory[0]) && subcategory[0].only_one_accessory
                    ? accessoriesArr.filter(
                          (el) =>
                              el.accessory.window_accessories_category_id === subcategory[0].id &&
                              el.accessory.id !== accessory.id
                      )
                    : [];
        }

        /** Można dodać tylko jedną sztukę z tej grupy */
        let accessoryFromSameCategory =
            Common.isDefined(category[0]) && category[0].only_single_accessory
                ? accessoriesArr.filter(
                      (el) =>
                          el.accessory.window_accessories_category_id === category[0].id &&
                          el.accessory.id === accessory.id
                  )
                : [];

        if (accessoryFromSameCategory.length) {
            return;
        }

        if (!accessoryFromSameCategory.length) {
            accessoryFromSameCategory =
                Common.isDefined(subcategory[0]) && subcategory[0].only_single_accessory
                    ? accessoriesArr.filter(
                          (el) =>
                              el.accessory.window_accessories_category_id === subcategory[0].id &&
                              el.accessory.id === accessory.id
                      )
                    : [];
        }

        if (
            this.config().IccConfig.Configurators.oneVentilator &&
            ['accessory', 'complementary_goods'].indexOf(this.configurationsService.conf.conf) ===
                -1 &&
            ['ventilator', 'ventilator_hole'].includes(accessory.type) &&
            accessoriesArr.some((o) => ['ventilator', 'ventilator_hole'].includes(o.accessory.type))
        ) {
            this.infoService.showInfo(
                this.translateService.instant('ACCESSORY|Można dodać tylko jeden nawiewnik')
            );
        } else if (
            ['accessory', 'complementary_goods'].indexOf(this.configurationsService.conf.conf) ===
                -1 &&
            accessoriesFromSameCategory.length &&
            this.config().IccConfig.Configurators.onlyOneAccessoryFromSameCategory
        ) {
            this.infoService.showInfo(
                this.translateService.instant(
                    'ACCESSORY|Można dodać tylko jeden dodatek z tej Kategorii. Aktualnie dodany dodatek z tej kategorii to {accessoryName}',
                    { accessoryName: accessoriesFromSameCategory[0].accessory.name }
                )
            );
        } else if (
            ['accessory', 'complementary_goods'].indexOf(this.configurationsService.conf.conf) ===
                -1 &&
            accessoryFromSameCategory.length
        ) {
            this.infoService.showInfo(
                this.translateService.instant(
                    'ACCESSORY|Można dodać tylko jedną sztukę dodatków z tej Kategorii. Aktualnie dodany dodatek z tej kategorii to {accessoryName}',
                    { accessoryName: accessoryFromSameCategory[0].accessory.name }
                )
            );
        } else {
            if (
                (this.config().IccConfig.Configurators.oneVentilator &&
                    ['ventilator', 'ventilator_hole'].includes(accessory.type)) ||
                (category[0] && category[0].only_single_accessory) ||
                (subcategory[0] && subcategory[0].only_single_accessory)
            ) {
                count = 1;
            }

            // Wybrany kolor akcesorium
            let colorId = null;
            if (Common.isObject(color) && Common.isDefined(color.id)) {
                colorId = color.id;
                this.eventBusService.post({
                    key: 'accessoryColorBasedOnMapping',
                    value: { colorId: Number(color.id), place: 'accessory' },
                });
            }

            // Wymiar elementu
            let amount = 0;

            // Masa elementu
            let weight = 0;

            // Szerokość
            let width = 0;

            // Wysokość
            let height = 0;

            if (
                ['renson', 'ventilator', 'ventilator_hole', 'windowsill'].includes(accessory.type)
            ) {
                width = parseFloat(accessory.width) || 0;
                height = parseFloat(accessory.height) || 0;
            }

            const user = this.userService.get();

            // cena za sztukę
            if (Number(accessory.price_type) === 0) {
                amount = 0;
                weight = parseFloat(accessory.weight) * count || 0;
                // cena za m2
            } else if (Number(accessory.price_type) === 1) {
                width = parseFloat(accessory.amount_width);
                height = parseFloat(accessory.amount_height);

                if (width && height) {
                    amount = (width * height) / 1000000;
                    weight =
                        ((count * width * height) / 1000000) * parseFloat(accessory.weight) || 0;
                } else {
                    this.infoService.showInfo(
                        this.translateService.instant(
                            'ACCESSORY|Uzupełnij wszystkie pola przed dodaniem akcesoriów.'
                        ),
                        null
                    );
                    return;
                }
                // cena za mb
            } else {
                const valAmount = parseFloat(accessory.amount);

                if (valAmount > 0) {
                    amount = valAmount;
                } else {
                    this.infoService.showInfo(
                        this.translateService.instant(
                            'ACCESSORY|Uzupełnij wszystkie pola przed dodaniem akcesoriów.'
                        ),
                        null
                    );
                    return;
                }

                weight = (amount / 1000) * parseFloat(accessory.weight) * count || 0;
            }

            // nowe akcesoria
            if (
                !accessoriesArr.some(
                    (el) =>
                        el.accessory.id === accessory.id &&
                        el.amount === amount &&
                        ((el.color === colorId && accessory.price_source === 'colors') ||
                            (accessory.price_source === 'table' &&
                                el.colorOptions === colorOptions) ||
                            (accessory.price_source === 'confColors' &&
                                accessory.selectedColor &&
                                el.accessory.selectedColor &&
                                accessory.selectedColor &&
                                accessory.selectedColor.core &&
                                accessory.selectedColor.inner &&
                                accessory.selectedColor.outer &&
                                el.accessory.selectedColor &&
                                typeof el.accessory.selectedColor === 'object' &&
                                el.accessory.selectedColor.core &&
                                el.accessory.selectedColor.inner &&
                                el.accessory.selectedColor.outer &&
                                el.accessory.selectedColor.core.id ===
                                    accessory.selectedColor.core.id &&
                                el.accessory.selectedColor.inner.id ===
                                    accessory.selectedColor.inner.id &&
                                el.accessory.selectedColor.outer.id ===
                                    accessory.selectedColor.outer.id))
                )
            ) {
                if (
                    (Number(accessory.price_type) === 0 &&
                        count > 0 &&
                        (accessory.price_source !== 'colors' ||
                            (Common.isDefined(color) && Common.isDefined(colorId)))) ||
                    (Number(accessory.price_type) !== 0 &&
                        count > 0 &&
                        amount > 0 &&
                        (accessory.price_source !== 'colors' ||
                            (Common.isDefined(color) && Common.isDefined(colorId))))
                ) {
                    let newPos = 0;
                    const accessoryClone = core.copy(accessory);
                    accessoryClone.color = core.copy(color);
                    delete accessoryClone.count;
                    delete accessoryClone.amount;
                    delete accessoryClone.position;
                    delete accessoryClone.amount_width;
                    delete accessoryClone.amount_height;
                    accessoriesArr.push({
                        count,
                        amount,
                        position,
                        weight,
                        width,
                        height,
                        colorOptions,
                        accessory: accessoryClone,
                        color: Common.isObject(color) ? color.id : null,
                        pos: newPos,
                        side: Common.isDefined(place)
                            ? Common.isDefined(place.id)
                                ? place.id
                                : place
                            : undefined,
                        category: Common.isDefined(category[0])
                            ? category[0].name
                            : Common.isDefined(subcategory[0])
                            ? subcategory[0].name
                            : null,
                        comment: accessory.comment,
                    });
                }
                // zwiększenei ilości
            } else {
                for (let i = 0; i < accessoriesArr.length; i++) {
                    if (
                        accessoriesArr[i].accessory.id === accessory.id &&
                        accessoriesArr[i].amount === amount &&
                        ((accessoriesArr[i].color === colorId &&
                            accessoriesArr[i].accessory.price_source === 'colors') ||
                            (accessoriesArr[i].accessory.price_source === 'table' &&
                                accessoriesArr[i].colorOptions === colorOptions) ||
                            (accessoriesArr[i].accessory.price_source === 'confColors' &&
                                accessory.selectedColor &&
                                accessoriesArr[i].accessory.selectedColor &&
                                accessory.selectedColor &&
                                accessory.selectedColor.core &&
                                accessory.selectedColor.inner &&
                                accessory.selectedColor.outer &&
                                accessoriesArr[i].accessory.selectedColor &&
                                accessoriesArr[i].accessory.selectedColor.core &&
                                accessoriesArr[i].accessory.selectedColor.inner &&
                                accessoriesArr[i].accessory.selectedColor.outer &&
                                accessoriesArr[i].accessory.selectedColor.core.id ===
                                    accessory.selectedColor.core.id &&
                                accessoriesArr[i].accessory.selectedColor.inner.id ===
                                    accessory.selectedColor.inner.id &&
                                accessoriesArr[i].accessory.selectedColor.outer.id ===
                                    accessory.selectedColor.outer.id))
                    ) {
                        accessoriesArr[i].count += count;
                        accessoriesArr[i].weight += weight;
                    }
                }
            }
        }

        accessory.count = this.getCountAccessory(
            accessory,
            forIt,
            conf,
            null
        );
        this.updateAccessories();
        this.priceService.count();
        if (conf.type !== 'accessory' && conf.type !== 'garage_door' && conf.type !== 'mosquito') {
            // Wywołanie przez EventBus - nowe serwisy parametersService nasłuchują 'icc-redraw'
            this.eventBusService.post({
                key: 'icc-redraw',
                value: 'accessories',
                conf
            });
        }
    }

    /**
     * Funkcja usuwajaca
     * @param  {object} place     Miejsce
     * @param  {object} accessory Akcesoria
     */
    remove(place, accessory, removeDependencyAccessory = false, processDependencies = true) {
        const pauseId = this.eventBusService.pause(['processedDependencies']);

        const conf = this.configurationsService.conf.Current;
        let newAccessoriesArr;
        if (
            !removeDependencyAccessory &&
            accessory.accessory?.fromDependency &&
            !accessory.accessory?.addedWithRemovableDependency
        ) {
            accessory.count = 1;
            return true;
        }

        newAccessoriesArr = conf.Accessories.filter(
            (el) =>
                String(el.amount) !== String(accessory.amount) ||
                el.accessory.id !== accessory.accessory.id ||
                (el.color !== accessory.color &&
                    accessory.accessory.price_source === 'colors') ||
                el.colorOptions !== accessory.colorOptions
        );
        conf.Accessories = newAccessoriesArr;
        this.updateAccessories();

        this.priceService.count();
        if (conf.type !== 'accessory' && conf.type !== 'garage_door' && conf.type !== 'mosquito') {
            // Wywołanie przez EventBus - nowe serwisy parametersService nasłuchują 'icc-redraw'
            this.eventBusService.post({
                key: 'icc-redraw',
                value: 'accessories',
                conf
            });
        }

        this.eventBusService.post({
            key: 'icc-redraw',
            value: 'frame',
        });

        if (this.config().IccConfig.Configurators.dependencies && processDependencies) {
            this.eventBusService.post({ key: 'processDependencies', value: null });
        }
        this.eventBusService.resume(['processedDependencies'], pauseId);
    }

    getAccessoryPrice(accessory: IccAccessory, listPrice = false) {
        const conf = this.configurationsService.conf.Current;
        if ('Components' in conf && (conf as any).Components?.fullDoor === false) {
            listPrice = true;
        }
        const offer = this.stateService.getCurrentOffer();
        const dealerId = offer ? offer.dealer_id : null;
        const customPrice = this.customPricesService.get(dealerId);
        let multipliers = this.discountsAndMultipliersService.getMultipliers();
        let multiplier = 1;
        multipliers = (multipliers && multipliers[dealerId]) || multipliers;

        if (multipliers) {
            multiplier = (multipliers.Supplement && multipliers.Supplement[0]) || 1;
            if (
                conf.type === 'accessory'
                //|| conf.type === 'complementary_goods'
            ) {
                multiplier = (multipliers.Commodity && multipliers.Commodity[0]) || 1;
            }
        }
        return this.priceBaseService.addMarginMarketFactor(
            this.priceAccessoryService.priceAccessory(
                accessory,
                (WindowActiveConfiguration.is(conf) && conf.Colors) || null,
                (customPrice || {}).WindowAccessory,
                WindowActiveConfiguration.is(conf) && conf.SideAccessories
                    ? conf.SideAccessories.sizes
                    : undefined,
                conf.type !== 'accessory' && conf.Width,
                conf.type !== 'accessory' && conf.Height,
                conf.type,
                multiplier,
                listPrice
            ),
            accessory.accessory.no_discount
        );
    }

    /**
     * Funkcja otwierajaca modal z akcesoriami
     * @param  {object} sash       Skrzydło
     * @param  {object} sashNumber Numer skrzydła
     */
    // eslint-disable-next-line max-statements
    openModalAccessories(
        sash?,
        sashNumber?,
        callback?,
        confType?,
        conf = this.configurationsService.conf.Current
    ): PromiseLike<void | { accessory: IccAccessoryAccessory; color?: IccAccessoryColor }> {
        let {accessories} = this.findAccessories(conf, confType || conf.type);
        const filteredCategories = this.filterWindowAccessoriesCategories(
            this.configuratorsDataService.data.windowAccessoriesCategories
        );
        let forIt = 'configuration';
        let sideIdx = '0';
        if (this.config().IccConfig.Configurators.tutorialAvailable) {
            this.eventBusService.post({
                key: 'tutorialSteps',
                value: 'accessories',
            });
        }

        if (
            Common.isString(sash) &&
            sash === 'drive' &&
            conf.type !== 'accessory' &&
            conf.type !== 'garage_door' &&
            conf.type !== 'mosquito'
        ) {
            forIt = 'drive';
            sideIdx = conf.RollerShutter.drive && conf.RollerShutter.drive.id;
        } else if (confType === 'roller_shutter') {
            forIt = 'roller_shutter';
        }

        if (this.currentConfiguratorService.conf !== 'accessory') {
            switch (forIt) {
                case 'configuration':
                case 'roller_shutter':
                    accessories = accessories.filter(
                        (el) =>
                            el.place?.indexOf('conf') > -1 &&
                            (el.type !== 'roller_control_dependent' ||
                                (el.type === 'roller_control_dependent' &&
                                    el.roller_shutter_drives_id &&
                                    el.roller_shutter_drives_id.includes(
                                        conf.type === 'roller_shutter' &&
                                            conf.RollerShutter.drive.id
                                    ) &&
                                    conf.type === 'roller_shutter' &&
                                    conf.RollerShutter.shutters.every(
                                        (shutter) => shutter.realWidth >= Number(el.min_width)
                                    )))
                    );
                    break;
                case 'drive':
                    accessories = accessories.filter(
                        (el) =>
                            el.roller_shutter_drives_id &&
                            el.roller_shutter_drives_id.includes(sideIdx) &&
                            el.type === 'roller_control'
                    );
                    break;
            }
            accessories = accessories.map((accessory) =>
                this.setAmountAccessory(accessory, forIt, conf, forIt === 'sash' ? sash : null)
            );
        } else {
            accessories = accessories.filter((access) => {
                const category = filteredCategories.filter(
                    (el) => el.id === access.window_accessories_category_id
                );
                return access.access_conf || !category.show;
            });
        }

        accessories = accessories.map((el) => {
            if (el.price_source === 'confColors' || el.color_mode === 'accessory_category_color') {
                this.setDefaultColors(el, conf);
            }
            if (isArray(el.colors_ids) && el.price_source === 'colors' && el.color_mode !== 'accessory_category_color') {
                const accessoryColor = this.colorsAll.find(
                    (color) =>
                        el.colors_ids &&
                        el.colors_ids.some((id) => Number(id) === Number(color.id))
                );
                if (accessoryColor) {
                    el.selectedColor = accessoryColor.id;
                }
            }
            if (!el.blockAmountChange) {
                el.amount = null;
            }
            return el;
        });

        const categories = filteredCategories.filter(
            (el) => filterAccessories(accessories, el.id).length > 0
        );

        const subcategoriesArr = this.subcategories.filter(
            (el) => filterAccessories(accessories, el.parent_id, el.id).length > 0
        );

        const modalInstance = this.modalService.open({
            pageComponent: AccessoriesListPageComponent,
            resolve: {
                hideTitle: () => this.config().filterAccessoriesByCategories,
                forIt: () => forIt,
                accessories: () => accessories,
                categories: () => categories,
                subcategories: () => {
                    return subcategoriesArr.map((el) => {
                        el.id *= 1;
                        el.parent_id *= 1;
                        return el;
                    });
                },
                sash: () => sash,
                sashNum: () => sashNumber,
                colors: () => this.colorsAll,
                noPrice: () => this.configurationsService.price.noPrice,
            },
        });

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });

        return modalInstance.result.then(
            (result: { accessory: IccAccessoryAccessory; color?: IccAccessoryColor }) => {
                if (callback && typeof callback === 'function') {
                    callback(result);
                } else {
                    if (result) {
                        this.add(sash, result.accessory, result.color);
                    }
                    if (this.config().IccConfig.Configurators.dependencies) {
                        this.eventBusService.post({ key: 'processDependencies', value: null });
                        this.priceService.count();
                    }
                    this.eventBusService.post({
                        key: 'icc-redraw',
                        value: 'frame',
                    });
                    this.timeLimitService.count();
                }
                return result;
            }
        );
    }

    /**
     * Funkcja liczaca akcesoria (w górę)
     * @param  {object} accessory Akcesoria
     */
    countUp(accessory) {
        accessory.count++;

        this.updateAccessories();
        this.priceService.count();

        this.eventBusService.post({
            key: 'icc-redraw',
            value: 'frame',
        });
    }

    /**
     * Funkcja liczaca akcesoria (w dół)
     * @param  {object} accessory Akcesoria
     */
    countDown(accessory) {
        if (accessory.count > 1) {
            accessory.count--;
        }

        this.updateAccessories();
        this.priceService.count();

        this.eventBusService.post({
            key: 'icc-redraw',
            value: 'frame',
        });
    }

    /**
     * Przelicz cenę
     */
    countPrice() {
        this.priceService.count();
    }

    private reApplyDefaultColors(conf): void {
        if (!conf?.Accessories) {
            return;
        }
        const categories = this.configuratorsDataService.data.windowAccessoriesCategories || [];
        const eligible = conf.Accessories.filter((acc: IccAccessory) =>
            acc.accessory.color_mode === 'accessory_category_color' && !acc.accessory.changedColor
        );

        eligible.forEach((acc: IccAccessory) => {
            const category = categories.find(
                (c) => String(c.id) === String(acc.accessory.window_accessories_category_id)
            );
            if (
                category?.color_mapping_source
                && category.color_mapping_source !== 'accessory'
                && category.color_mapping_source !== 'accessory_category'
            ) {
                this.setDefaultColors(acc.accessory, conf);
            }
        });

        eligible.forEach((acc: IccAccessory) => {
            const category = categories.find(
                (c) => String(c.id) === String(acc.accessory.window_accessories_category_id)
            );
            if (
                category?.color_mapping_source === 'accessory'
                || category?.color_mapping_source === 'accessory_category'
            ) {
                this.setDefaultColors(acc.accessory, conf);
            }
        });
    }

    /**
     * Ustawia domyślne kolory dodatku
     *
     * @param {any} accessory Dodatek
     */
    setDefaultColors(accessory: IccAccessoryAccessory, conf) {
        if (accessory.material === 'wood') {
            accessory.selectedWood = this.woodService.woods[0];
        } else {
            accessory.selectedWood = null;
        }

        if (accessory.color_mode === 'accessory_category_color' && !accessory.changedColor) {
            const categories = this.configuratorsDataService.data.windowAccessoriesCategories || [];
            const category = categories.find(
                (c) => String(c.id) === String(accessory.window_accessories_category_id)
            );

            if (category?.color_mapping_source) {
                const availableIds = (accessory.colors_ids || []).map(Number);
                const availableColors = (this.colorsAll || []).filter(
                    c => availableIds.includes(Number(c.id))
                );

                const findByConstrColorId = (constrColorId: number | undefined): IccAccessoryColor | undefined => {
                    if (!constrColorId) {
                        return undefined;
                    }
                    const mappedIds = this.colorMappingService.getMappedHandleColorIds(constrColorId);
                    return availableColors.find(c => mappedIds.includes(Number(c.id)));
                };

                const findByHandleColorId = (handleColorId: number | string | undefined): IccAccessoryColor | undefined => {
                    if (!handleColorId) {
                        return undefined;
                    }
                    const mappedIds = this.colorMappingService.getColors(Number(handleColorId), 'accessory', 'accessory');
                    return availableColors.find(c => Number(c.id) === Number(handleColorId))
                        || availableColors.find(c => mappedIds.includes(Number(c.id)));
                };

                const source = category.color_mapping_source;
                let matchedColor: IccAccessoryColor | undefined;

                if (source !== 'accessory' && source !== 'accessory_category') {
                    const constrColorId = this.getConstrColorIdForSource(conf, source);
                    matchedColor = findByConstrColorId(constrColorId);
                }

                if (source === 'accessory' && category.color_mapping_accessory_id) {
                    const sourceAcc = (conf.Accessories || []).find(
                        (acc: IccAccessory) =>
                            String(acc.accessory.id) === String(category.color_mapping_accessory_id)
                    );
                    const selected = sourceAcc?.accessory?.selectedColor;
                    if (selected != null) {
                        if (typeof selected === 'string' || typeof selected === 'number') {
                            matchedColor = findByHandleColorId(selected);
                        } else {
                            matchedColor = findByConstrColorId((selected as any)?.outer?.id);
                        }
                    }
                    if (
                        !matchedColor
                        && conf.doorHardwareDictionary?.doorHardware?.[category.color_mapping_accessory_id]
                    ) {
                        const hardwareColorId = this.getHardwareItemColorId(
                            conf,
                            category.color_mapping_accessory_id
                        );
                        if (hardwareColorId != null) {
                            matchedColor = findByHandleColorId(hardwareColorId);
                        }
                    }
                }

                if (source === 'accessory_category' && category.color_mapping_accessory_category_id) {
                    const sourceAcc = (conf.Accessories || []).find(
                        (acc: IccAccessory) =>
                            String(acc.accessory.window_accessories_category_id)
                            === String(category.color_mapping_accessory_category_id)
                    );
                    const selected = sourceAcc?.accessory?.selectedColor;
                    if (selected != null) {
                        if (typeof selected === 'string' || typeof selected === 'number') {
                            matchedColor = findByHandleColorId(selected);
                        } else {
                            matchedColor = findByConstrColorId((selected as any)?.outer?.id);
                        }
                    }
                }


                if (matchedColor) {
                    accessory.selectedColor = String(matchedColor.id);
                } else  {
                    accessory.selectedColor = String(availableColors[0].id);
                }
            }
            return;
        }

        const outerColor = this.newColorsService.getDefaultElementColor(conf, {
            element: 'accessory',
            side: 'outer',
            accessory,
        });
        const outerConstrColor = this.newColorsService.getMatchingConstrColor(
            conf,
            outerColor,
            {},
            {
                element: 'accessory',
                side: 'outer',
                accessory,
            }
        );
        const innerColor = this.newColorsService.getDefaultElementColor(conf, {
            element: 'accessory',
            side: 'inner',
            accessory,
        });
        const innerConstrColor = this.newColorsService.getMatchingConstrColor(
            conf,
            innerColor,
            {},
            {
                element: 'accessory',
                side: 'inner',
                accessory,
            }
        );
        accessory.selectedColor = {
            outer: outerConstrColor
                ? {
                      ...outerConstrColor,
                      isDefault: true,
                  }
                : null,
            inner: innerConstrColor
                ? {
                      ...innerConstrColor,
                      isDefault: true,
                  }
                : null,
        };
        const coreColor = this.newColorsService.getDefaultElementColor(conf, {
            element: 'accessory',
            side: 'core',
            accessory,
        });
        accessory.selectedColor.core = coreColor
            ? {
                  ...coreColor,
                  isDefault: true,
              }
            : null;
    }

    private getHardwareItemColorId(conf, accessoryId: number | string): number | null {
        const doorHardware = conf.doorHardware;
        if (!doorHardware) {
            return null;
        }
        const id = String(accessoryId);
        if (
            String(doorHardware.plates?.mainLock) === id
            || String(doorHardware.plates?.additionalLock) === id
        ) {
            return doorHardware.plates?.color ?? null;
        }
        if (
            String(doorHardware.outerPlates?.mainLock) === id
            || String(doorHardware.outerPlates?.additionalLock) === id
        ) {
            return doorHardware.outerPlates?.color ?? null;
        }
        if (String(doorHardware.cylinders?.mainLock) === id) {
            return doorHardware.cylinders?.mainLockColor ?? doorHardware.cylinders?.color ?? null;
        }
        if (String(doorHardware.cylinders?.additionalLock) === id) {
            return doorHardware.cylinders?.additionalLockColor ?? null;
        }
        if (String(doorHardware.pull) === id) {
            return doorHardware.pullColor ?? null;
        }
        if (String(doorHardware.electronicLock?.id) === id) {
            return doorHardware.electronicLock?.color ?? null;
        }
        if (String(doorHardware.doorViewer) === id) {
            return doorHardware.doorViewerColor ?? null;
        }
        if (String(doorHardware.decorativeHingeCovers) === id) {
            return doorHardware.decorativeHingeCoversColor ?? null;
        }
        if (String(doorHardware.type) === id) {
            return doorHardware.color ?? null;
        }
        return null;
    }

    /**
     * Zwraca ID koloru konstrukcji (IccConstructColor.id) z conf.Colors
     * dla danego źródła mapowania (sash_inner/sash_outer/frame_inner/frame_outer/sash_hinge/frame_hinge).
     */
    private getConstrColorIdForSource(conf, source: string): number | undefined {
        const hingeSide: 'inner' | 'outer' = conf.Sashes?.some((sash: any) => sash.type?.type === 'DRA')
            ? 'inner'
            : conf.Sashes?.some((sash: any) => sash.type?.type === 'DOA')
                ? 'outer'
                : 'outer';

        switch (source) {
            case 'sash_inner':
                return Number(conf.Colors?.sash?.inner?.id) || undefined;
            case 'sash_outer':
                return Number(conf.Colors?.sash?.outer?.id) || undefined;
            case 'sash_hinge':
                return Number(conf.Colors?.sash?.[hingeSide]?.id) || undefined;
            case 'frame_inner':
                return this.colorMappingService.getWindowColorId(conf, 'inner') ?? undefined;
            case 'frame_outer':
                return this.colorMappingService.getWindowColorId(conf, 'outer') ?? undefined;
            case 'frame_hinge':
                return this.colorMappingService.getWindowColorId(conf, hingeSide) ?? undefined;
            default:
                return undefined;
        }
    }

    setAmountAccessory(accessory, forIt, conf, sash = null) {
        let accessoryData = accessory;
        if (accessory.accessory) {
            accessoryData = accessory.accessory;
        }
        let amount;
        if(forIt === 'sash' && accessoryData.type === 'renson') {
            amount = sash.intSashes && sash.intSashes.length
                ? sash.intSashes.filter(s => !s.ry)
                    .map(s => s.glazingSizes.width)
                    .reduce((a, b) => a + b, 0)
                : sash.glazingSizes.width;
        } else if (forIt === 'sash') {
            amount = this.calcAmountFromFormula(accessoryData.amount_sash_formula, conf, sash);
        } else if (forIt === 'configuration' || forIt === 'roller_shutter') {
            amount = this.calcAmountFromFormula(accessoryData.amount_conf_formula, conf, sash);
        }
        accessory.count = accessory.count || 1;
        if (amount) {
            if (Number(accessoryData.price_type) === 2) {
                accessory.amount = amount;
                accessory.blockAmountChange = true;
            } else if (Number(accessoryData.price_type) === 0) {
                accessory.count = amount;
                accessory.blockCountChange = true;
            }
        }
        return accessory;
    }

    getCountAccessory(accessory, forIt, conf, sash = null) {
        let amount;
        if (forIt === 'sash') {
            amount = this.calcAmountFromFormula(accessory.amount_sash_formula, conf, sash);
        } else if (forIt === 'configuration') {
            amount = this.calcAmountFromFormula(accessory.amount_conf_formula, conf, sash);
        }
        if (amount && Number(accessory.price_type) === 0) {
            return amount;
        }
        return 1;
    }

    calcAmountFromFormula(formula, conf, sash = null) {
        if (formula) {
            let value = 0;
            formula = core.parseJson(formula);
            let symbol = 'plus';
            formula.forEach((block) => {
                if (['plus', 'minus'].indexOf(block.type) > -1) {
                    symbol = block.type;
                } else {
                    let valueToAdd = 0;
                    switch (block.type) {
                        case 'number':
                            valueToAdd = Number(block.value);
                            break;
                    }
                    if (symbol === 'minus') {
                        value -= valueToAdd;
                    } else {
                        value += valueToAdd;
                    }
                }
            });
            return value;
        }
        return 0;
    }

    markAccessoryAsAddedWithDependency(condition, conf, addedWithDependency = false) {
        const accessory = this.getConstructionAccessory(condition.value, conf);
        if (accessory && accessory.accessory) {
            accessory.accessory.addedWithDependency = addedWithDependency;

            if (condition.type === 'addRemovableAccessoryToConstruction') {
                accessory.accessory.addedWithRemovableDependency = true;
            }
        }
    }

    markAccessoryWithDependencyId(condition, conf, dependencyId) {
        const accessory = this.getConstructionAccessory(condition.value, conf);
        if (accessory && accessory.accessory) {
            accessory.accessory.fromDependency = dependencyId;

            if (condition.type === 'addRemovableAccessoryToConstruction') {
                accessory.accessory.addedWithRemovableDependency = true;
            }
        }
    }

    markAccessoryWithDependencyAssociationIds(accessoryId, conf, dependencyAssociationIds) {
        const accessory = this.getConstructionAccessory(accessoryId, conf);
        if (accessory && accessory.accessory)
            accessory.accessory.dependencyAssociationIds = dependencyAssociationIds;
    }

    getConstructionAccessory(accessoryId, conf) {
        const accessory =
            Common.isArray(conf.Accessories) &&
            conf.Accessories.find((el) => el.accessory && el.accessory.id == accessoryId);

        if (accessory && accessory.accessory && accessory.accessory.id) {
            return accessory;
        }
        return undefined;
    }

    removeInvalidFrameDependentAccessories(
        frameProfileId: string | number,
        conf = this.configurationsService.conf.Current as
            | DoorActiveConfiguration
            | WindowActiveConfiguration
    ) {
        //  Accessories for the entire construction
        const filteredAccessories = conf.Accessories.filter((a: IccAccessory) => {
            if (a.accessory.dependent_on_frame_profile) {
                return a.accessory.frame_profiles_ids.map(Number).includes(Number(frameProfileId));
            }
            return a;
        });
        conf.Accessories = filteredAccessories;
    }

    setAccessoryColor(accessory: IccAccessory, colors: IccAccessoryColor[]) {
        if (colors) {
            accessory.accessory.changedColor = true;
            accessory.color =
                accessory.accessory.selectedColor &&
                <IccAccessoryColor>(
                    colors.find((c) => Number(c.id) === Number(accessory.accessory.selectedColor))
                );
            accessory.accessory.color = accessory.color;
        }
    }

    filterWindowAccessoriesCategories(categories) {
        return (categories || []).filter((el) => !el.show && el.parent_id === null);
    }

    validateShutterDriveDependantAccessories(conf = this.configurationsService.conf.Current) {
        if (conf.type === 'roller_shutter') {
            const drive = conf.RollerShutter.drive;
            const removedAccessories = [];
            if (drive) {
                conf.Accessories = conf.Accessories.filter((acc) => {
                    if (
                        acc.accessory.type === 'roller_control_dependent' &&
                        (!acc.accessory.roller_shutter_drives_id ||
                            !acc.accessory.roller_shutter_drives_id.includes(drive.id) ||
                            !conf.RollerShutter.shutters.every(
                                (shutter) => shutter.realWidth >= Number(acc.accessory.min_width)
                            ))
                    ) {
                        removedAccessories.push(acc.accessory);
                        return false;
                    }
                    return true;
                });
            }
            if (removedAccessories.length > 0) {
                this.issuesService.simpleRegister(
                    'removed-roller-control-dependent',
                    `Dodatek ${removedAccessories.map(el => el.name).join(', ')} został usunięty, ponieważ nie spełnia już wymagań konfiguracyjnych.`,
                    this.translateService.instant(
                        'ACCESSORY|Dodatek {accessory} został usunięty, ponieważ nie spełnia już wymagań konfiguracyjnych.',
                        { accessory: removedAccessories.map(el => el.name).join(', ') }
                    ),
                    conf,
                    {
                        logLevel: IssueLevel.NONE,
                        blockAddToOffer: false,
                        noPrice: false,
                    }
                );
            }
        }
    }
}

export function filterAccessories(
    accessories: IccAccessoryAccessory[],
    categoryId: string | number,
    subcatId?: string | number
): IccAccessoryAccessory[] {
    if (isUndefined(categoryId) || categoryId === null || categoryId === '') {
        return accessories;
    } else {
        return accessories.filter(gl => {
            if (gl.parent_id == categoryId || gl.window_accessories_category_id == categoryId) {
                if (isUndefined(subcatId) || subcatId === null || subcatId === '') {
                    return true;
                } else if (gl.window_accessories_category_id == subcatId) {
                    return true;
                }
            }
            return false;
        });
    }
}
